import { List as ImList, Record as ImRecord, Set as ImSet } from 'immutable';

import type { CashFlowTransaction } from 'companion-app-components/flux/transactions/transactionsTypes';
import type { Account } from 'companion-app-components/flux/accounts/accountsTypes';
import { getLogger } from 'companion-app-components/utils/core';

import { newRegFields } from './utils';
import * as transactionsConfig from './transactionsConfig';

const log = getLogger('components/TransactionRegister/index.tsx');

// This is a globalState object that maintains state based on a map whose key
// is the account ID. As the register changes to view different accounts,
// the state will be persisted into and loaded from this global state object.
//
// TODO: This needs to be reworked.
//   - At a minimum, state should be stored in the global auth object so at
//     least it will get cleared when user logs out.
//   - Would be better to do this in the redux store...
//

export type RegisterUIStateType = {
  initNumberTxnsShown?: number;
  txnBeingEdited?: null | CashFlowTransaction;
  txnDirty?: boolean;
  currEditFieldName?: string;
  showOnlineBalance?: boolean;
  showEditMenu?: null | boolean;
  tryNextRow?: null | boolean;
  showNewTxn?: boolean;
  showDocumentAdd?: boolean;
  showDocumentBrowserTxn?: null | boolean;
  showSplitWindow?: boolean;
  showBulkEdit?: boolean;
  simpleEdit?: boolean;
  regEditableFields?: ImList<string>;
  selectedTxns?: ImSet<CashFlowTransaction>;
  sortAnimation?: boolean;
  flashTx?: null | boolean;
  openSections?: ImSet<string>;
  showAttachmentColumnInHeader?: boolean;
  txnsFirstLoaded?: string | null;
  searchBoxFilter?: string;
  scrollToId?: null | string;
  dataToDownload?: null | Record<string, any>;
  allSelected?: boolean;
  headerFields?: null | ImList<string>;
  lastAccountEntered?: null | Account;
  detailsRefSaveFn?: () => void;
  reminderSetting?: string | number;
  filterObject?: null | Record<string, any>;
  showSplitsAndDetails?: boolean;
  accountIds?: any,
};

/* eslint-disable react/sort-comp */
const RegisterUIState = ImRecord<RegisterUIStateType>({
  initNumberTxnsShown: 20,

  // Transient UI State
  txnBeingEdited: null,
  txnDirty: false,
  currEditFieldName: 'payee',
  showOnlineBalance: false,
  showEditMenu: null,
  tryNextRow: null,
  showNewTxn: false,
  showDocumentAdd: false,
  showDocumentBrowserTxn: null,
  showSplitWindow: false,
  showBulkEdit: false,
  simpleEdit: true,
  regEditableFields: ImList(),
  selectedTxns: ImSet(),
  sortAnimation: true,
  flashTx: null,
  openSections: ImSet([]),
  showAttachmentColumnInHeader: false,
  txnsFirstLoaded: 'default',
  searchBoxFilter: '',
  scrollToId: null,
  dataToDownload: null, // { headers: [], data: [] }
  allSelected: false,
  headerFields: null,
  lastAccountEntered: null,
  detailsRefSaveFn: null,
  reminderSetting: 0,
  filterObject: null,
});

export const initShowTransactions = (passedProps, minNumVisible) => {
  let openSections = ImSet([]);
  if (passedProps.acctTxns) {
    let visibleCount = 0;
    passedProps.acctTxns.keySeq().forEach((key) => {
      if (visibleCount < minNumVisible) {
        const shortKey = key.slice(key.indexOf(':') + 1);
        openSections = openSections.add(shortKey as never);
        visibleCount += passedProps.acctTxns.get(key).size;
      }
    });
  }

  return openSections;
};

const initialUIStateConfig = (props) => {
  const ret = new RegisterUIState({
    regEditableFields: newRegFields(props.doubleAmounts),
    openSections: initShowTransactions(props, 300),
    headerFields: props.regFieldsOrder || ImList(transactionsConfig.getColumnDefaults()),
    reminderSetting: props.reminderSetting || 0,
  });

  log.log('INIT REGISTER STATE WITH ->', props, ret);
  return ret;
};

// NOTE: Only props established before USSTateConfig is called in the compose at the bottom of this
// class are going to be available to this section.
export const uiStateConfig = {
  name: (props) => {
    let ret = `TxRegId_${props.wrapperId}:${props.accountNodeId}`;
    if (!props.accountNodeId) {
      if (props.accountIds && props.accountIds.size === 1) {
        ret = `${ret}${props.accountIds.first()}`;
      } else if (props.accountIds) {
        props.accountIds.forEach((v) => {
          ret = `${ret}:${v}`;
        });
      }
    }
    return ret;
  },

  state: initialUIStateConfig,

  persist: true,
};
